/**
 * FLANK DESIGN TOKENS
 * Professional Voice Coaching Platform
 *
 * Use these tokens for consistent styling across the application.
 * For Tailwind classes, use the equivalent class names (e.g., 'bg-flank-teal').
 */

// ===========================================
// COLOR PALETTE
// ===========================================

export const colors = {
  // PRIMARY BRAND COLORS
  flank: {
    teal: '#5BB2C3',      // Primary brand color - Fresh, modern, approachable
    darkBlue: '#192243',  // Dark foundations - Headers, text, depth
    lightBlue: '#457AC9', // Secondary accent - Links, highlights, CTAs
  },

  // Legacy Midnight Green palette (kept for backward compatibility)
  midnight: {
    50: '#E8F0F3',
    100: '#C5D9E0',
    200: '#9DBFCC',
    300: '#6A9FB0',
    400: '#3D7A8C',
    500: '#1A5568',
    600: '#10405A', // Legacy brand color
    700: '#0B2E36', // Deep backgrounds, headers
    800: '#0A2E36', // Gunmetal - primary text
    900: '#061A1F',
  },

  // Accent: Fueld Green (Success, Growth, Active States)
  fueldGreen: {
    50: '#F0F9E8',
    100: '#D4F0C2',
    200: '#B5E599',
    300: '#91D968',
    400: '#6FC93D',
    500: '#8BC34A', // Fueld Primary Green
    600: '#70A83B',
    700: '#558B2F',
  },

  // Energy: Celery Orange (CTAs, Coach Accents)
  celery: {
    50: '#FFF3E0',
    100: '#FFE0B2',
    200: '#FFCC80',
    300: '#FFB74D',
    400: '#FFA726',
    500: '#DE5303', // Celery Orange Primary
    600: '#F94B23', // Fueld Brand Orange
    700: '#AB4002',
    800: '#792D01',
    900: '#471A01',
  },

  // Additional Fueld Palette
  breakfastBlue: '#0097D7',
  lunchTeal: '#00D7AC',
  dinnerGold: '#D7AC00',
  snacksPurple: '#D70097',

  // Neutral
  white: '#FFFFFF',
  black: '#000000',
} as const;

// ===========================================
// SEMANTIC TOKENS
// ===========================================

export const semanticColors = {
  light: {
    background: colors.white,
    surface: '#F8FAFB',
    surfaceElevated: colors.white,
    textPrimary: colors.midnight[800],
    textSecondary: colors.midnight[400],
    textMuted: colors.midnight[300],
    border: colors.midnight[100],
    borderFocus: colors.midnight[600],
  },
  dark: {
    background: colors.midnight[700],
    surface: colors.midnight[600],
    surfaceElevated: colors.midnight[500],
    textPrimary: '#F8FAFB',
    textSecondary: colors.midnight[200],
    textMuted: colors.midnight[300],
    border: colors.midnight[500],
    borderFocus: colors.fueldGreen[500],
  },
} as const;

export const functionalColors = {
  success: colors.fueldGreen[500],
  successSubtle: colors.fueldGreen[50],
  warning: colors.dinnerGold,
  warningSubtle: '#FFF9E0',
  error: colors.snacksPurple,
  errorSubtle: '#FFF0FB',
  info: colors.breakfastBlue,
  infoSubtle: '#E8F7FC',
} as const;

// ===========================================
// BRAND TOKENS
// ===========================================

export const brandColors = {
  // New Flank brand colors (primary)
  teal: colors.flank.teal,
  darkBlue: colors.flank.darkBlue,
  lightBlue: colors.flank.lightBlue,
  // Legacy mappings
  primary: colors.flank.teal,
  accent: colors.fueldGreen[500],
  energy: colors.celery[600],
} as const;

// ===========================================
// TYPOGRAPHY
// ===========================================

export const fontFamily = {
  sans: "'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif",
  display: "'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif",
  mono: "'JetBrains Mono', 'Fira Code', Consolas, monospace",
} as const;

export const fontSize = {
  xs: '12px',
  sm: '14px',
  base: '16px',
  lg: '18px',
  xl: '20px',
  '2xl': '24px',
  '3xl': '30px',
  '4xl': '36px',
  '5xl': '48px',
} as const;

export const fontWeight = {
  normal: 400,
  medium: 500,
  semibold: 600,
  bold: 700,
  extrabold: 800,
} as const;

export const lineHeight = {
  tight: 1.25,
  normal: 1.5,
  relaxed: 1.75,
} as const;

// ===========================================
// SPACING
// ===========================================

export const spacing = {
  0: '0',
  1: '4px',
  2: '8px',
  3: '12px',
  4: '16px',
  5: '20px',
  6: '24px',
  8: '32px',
  10: '40px',
  12: '48px',
  16: '64px',
} as const;

// ===========================================
// BORDER RADIUS
// ===========================================

export const borderRadius = {
  sm: '4px',
  md: '8px',
  lg: '12px',
  xl: '16px',
  '2xl': '24px',
  full: '9999px',
} as const;

// ===========================================
// SHADOWS
// ===========================================

export const shadows = {
  xs: '0 1px 2px rgba(10, 46, 54, 0.05)',
  sm: '0 1px 3px rgba(10, 46, 54, 0.08)',
  md: '0 4px 12px rgba(10, 46, 54, 0.12)',
  lg: '0 8px 24px rgba(10, 46, 54, 0.16)',
  xl: '0 16px 48px rgba(10, 46, 54, 0.2)',
} as const;

// ===========================================
// ANIMATION
// ===========================================

export const animation = {
  fast: '150ms',
  normal: '200ms',
  slow: '300ms',
  easing: {
    default: 'cubic-bezier(0.4, 0, 0.2, 1)',
    in: 'cubic-bezier(0.4, 0, 1, 1)',
    out: 'cubic-bezier(0, 0, 0.2, 1)',
    inOut: 'cubic-bezier(0.4, 0, 0.2, 1)',
  },
} as const;

// ===========================================
// COMPLETE THEME OBJECT
// ===========================================

export const flankTheme = {
  colors,
  semanticColors,
  functionalColors,
  brandColors,
  fontFamily,
  fontSize,
  fontWeight,
  lineHeight,
  spacing,
  borderRadius,
  shadows,
  animation,
} as const;

export type FlankTheme = typeof flankTheme;
export type Colors = typeof colors;
export type BrandColors = typeof brandColors;

export default flankTheme;
